/**
 * @fileoverview Own the matter, client, title and category fields of the Redux calendar draft. `kc-internal-frontend/src/components/Calendar/EventForm.js` composes it and `kc-internal-frontend/src/store/CalendarInput/action.js` owns draft transitions. Choosing a known matter looks up its client through `kc-internal-backend/routes/calendar_routes.py` and, for new events only, fills the matter's custom calendar title or a "Client v. Defendant" title. Only a user's client edit triggers the reverse client-to-matter lookup, and lookups are sequenced so a stale response or a closed form never repopulates the draft. Existing events load their dates through `kc-internal-frontend/src/components/Calendar/calendarFormDates.js`.
 */

import moment from 'moment-timezone';
import { eventFormDate } from '../Calendar/calendarFormDates';
import { useSelector, useDispatch } from 'react-redux';
import api from '../../api';
import axios from 'axios';
import { useEffect, useRef } from 'react';

import {
  setMatters,
  setClientName,
  setMatterNumbers,
  setMatterNumber,
  setClients,
  setDefendants,
  setFilteredClients,
  setFilteredMatters,
  setTitle,
  setTitle2,
  setSelectedCategory,
  setKey,
  setAllday,
  setDate,
  setDaterange,
  setNotes,
  setFileIds,
  setUploadedFiles,
  setCreateTask,
  setTimeZone,
  setLocation,
  setSoftDeadlinesEnabled,
  setSoftDeadlines } from
'../../store/CalendarInput/action';
import { AutoComplete } from '../shared/AppAutoComplete';
import { InputText } from '../shared/AppTextInput';
import { Dropdown } from '../shared/AppDropdown';
import AppIcon from "../shared/AppIcon";



const PrimaryInfo = ({ clientNameRef, matterNumberRef }) => {
  const dispatch = useDispatch();
  const matters = useSelector((state) => state.calendarInput.matters);
  const matterNumbers = useSelector((state) => state.calendarInput.matterNumbers);
  const clients = useSelector((state) => state.calendarInput.clients);
  const defendants = useSelector((state) => state.calendarInput.defendants);
  const currentlyEditingEvent = useSelector((state) => state.calendarInput.currentlyEditingEvent);
  const categories = useSelector((state) => state.calendarInput.categories);
  const selectedCategory = useSelector((state) => state.calendarInput.selectedCategory);
  const clientName = useSelector((state) => state.calendarInput.clientName);
  const matterNumber = useSelector((state) => state.calendarInput.matterNumber);
  const title = useSelector((state) => state.calendarInput.title);
  const title2 = useSelector((state) => state.calendarInput.title2);

  const filteredClients = useSelector((state) => state.calendarInput.filteredClients);
  const filteredMatters = useSelector((state) => state.calendarInput.filteredMatters);
  const location = useSelector((state) => state.calendarInput.location);
  const lookupRequestRef = useRef(0);
  clientNameRef.current = clientName;
  matterNumberRef.current = matterNumber;

  useEffect(() => () => {
    // Pending lookups must not repopulate a closed/reset form.
    lookupRequestRef.current += 1;
  }, []);

  useEffect(() => {
    document.title = "Calendar Input";
    axios.all([
    api.get('matters'),
    api.get('clients'),
    api.get('defendants')]
    )
    .then(axios.spread(

    (mattersResponse, clientsResponse, defendantsResponse) => {
      const matterNumbers = mattersResponse.data.map((matter) => matter.MatterNumber);
      const formattedClients = clientsResponse.data.map((client) => client.ClientName);
      dispatch(setMatters(mattersResponse.data));
      dispatch(setMatterNumbers(matterNumbers));
      dispatch(setClients(formattedClients));
      dispatch(setDefendants(defendantsResponse.data));
      console.log('dispatched matters, matterNumbers, clients, defendants');
    }))
    .catch((error) => {
      console.log(error);
    });
  }, []);

  useEffect(

  () => {
    console.log('Matters:', matters);
    console.log('Clients:', clients);
    console.log('Defendants:', defendants);
    console.log('Matter Numbers:', matterNumbers);
  }, [matters, clients, defendants, matterNumbers]);



  const findMatchingClientName = (mn) => {
    const matterNum = mn || matterNumberRef.current;

    if (!matterNum) {
      console.warn('No matter number provided to findMatchingClientName');
      return;
    }

    console.log('Finding matching client name for matter:', matterNum);

    const requestId = ++lookupRequestRef.current;
    api.get(`find_matching_client_and_defendant/${encodeURIComponent(matterNum)}`)
    .then(

    (response) => {
      if (requestId !== lookupRequestRef.current) return;
      console.log('API response:', response.data);
      const { _clientName, _defendantNames, _customCalendarTitle } = response.data;

      // Set client name if found
      if (_clientName) {
        dispatch(setClientName(_clientName));
      }

      // Only auto-populate title for new events, not when editing
      if (!currentlyEditingEvent && _defendantNames) {
        if (_customCalendarTitle) {
          // Use custom title from database
          dispatch(setTitle(_customCalendarTitle));
          console.log('Setting custom title:', _customCalendarTitle);
        } else if (_clientName) {
          // Construct title from client and defendant names
          try {
            // Backend returns _defendantNames as a string (already joined)
            const defendantNamesStr = _defendantNames;

            // Extract last name from client name using matter number letters
            // Matter format: XXXXX where XXX (positions 1-3) match start of last name
            const nameParts = _clientName.replace(/'/g, '').split(" ");

            let clientLastName = _clientName; // Default to full name if extraction fails

            if (matterNum.length >= 4) {
              const lastNameLettersFromMatter = matterNum.substring(1, 4).toUpperCase();
              const matchingIndex = nameParts.findIndex((part) =>
              part.toUpperCase().startsWith(lastNameLettersFromMatter)
              );

              if (matchingIndex >= 0) {
                // Extract everything from the matching index onwards as the last name
                clientLastName = nameParts.slice(matchingIndex).join(" ");
              } else {
                // If no match found, use the last word as last name
                clientLastName = nameParts[nameParts.length - 1];
              }
            }

            const title = `${clientLastName} v. ${defendantNamesStr}`;
            console.log('Setting constructed title:', title);
            dispatch(setTitle(title));
          } catch (err) {
            console.error('Error constructing title:', err);
            // Fallback: just set defendant names as title
            dispatch(setTitle(_defendantNames));
          }
        } else {
          // Only defendant names available
          dispatch(setTitle(_defendantNames));
        }
      }
    })
    .catch((error) => {
      console.error('Error finding client and defendant:', error);
    });
  };



  const findMatchingMatterNumber = (clientNameValue) => {

    if (!clientNameValue) {
      console.warn('No client name provided to findMatchingMatterNumber');
      return;
    }

    console.log('Finding matching matter for client:', clientNameValue);

    const requestId = ++lookupRequestRef.current;
    api.get(`find_matching_matter_and_defendant/${encodeURIComponent(clientNameValue)}`)
    .then(

    (response) => {
      if (requestId !== lookupRequestRef.current) return;
      console.log('API response:', response.data);
      const { _matterNumber, _defendantNames, _customCalendarTitle } = response.data;

      if (_matterNumber) {
        // Only update if the matter number changed (avoid infinite loops)
        if (_matterNumber !== matterNumberRef.current) {
          dispatch(setMatterNumber(_matterNumber));

          // Only auto-populate title for new events, not when editing
          if (!currentlyEditingEvent) {
            if (_customCalendarTitle) {
              // Use custom title from database
              dispatch(setTitle(_customCalendarTitle));
              console.log('Setting custom title:', _customCalendarTitle);
            } else if (_defendantNames) {
              // Construct title from client and defendant names
              try {
                // Backend returns _defendantNames as a string (already joined)
                const defendantNamesStr = _defendantNames;

                // Extract last name from client name using matter number letters
                const nameParts = clientNameValue.replace(/'/g, '').split(" ");

                let clientLastName = clientNameValue; // Default to full name if extraction fails

                if (_matterNumber.length >= 4) {
                  const lastNameLettersFromMatter = _matterNumber.substring(1, 4).toUpperCase();
                  const matchingIndex = nameParts.findIndex((part) =>
                  part.toUpperCase().startsWith(lastNameLettersFromMatter)
                  );

                  if (matchingIndex >= 0) {
                    // Extract everything from the matching index onwards as the last name
                    clientLastName = nameParts.slice(matchingIndex).join(" ");
                  } else {
                    // If no match found, use the last word as last name
                    clientLastName = nameParts[nameParts.length - 1];
                  }
                }

                const title = `${clientLastName} v. ${defendantNamesStr}`;
                console.log('Setting constructed title:', title);
                dispatch(setTitle(title));
              } catch (err) {
                console.error('Error constructing title:', err);
                // Fallback: just set defendant names as title
                dispatch(setTitle(_defendantNames));
              }
            }
          }
        }
      }
    })
    .catch((error) => {
      console.error('Error finding matter and defendant:', error);
    });
  };

  const handleClientChange =

  ({ value }) => {
    if (value === clientName) return;
    lookupRequestRef.current += 1;
    dispatch(setClientName(value));
    // Reverse lookup only follows a user edit. A client populated from a matter
    // must never select that client's first matter, including when focus moves.
    if (value && clients?.includes(value)) {
      findMatchingMatterNumber(value);
    }
  };

  const handleMatterChange =

  ({ value }) => {
    if (value === matterNumber) return;
    lookupRequestRef.current += 1;
    dispatch(setMatterNumber(value));
  };



  useEffect(() => {
    if (matterNumbers?.includes(matterNumber) && matterNumber) {
      console.log('Matter number changed, finding matching client:', matterNumber);
      findMatchingClientName(matterNumber);
    }
  }, [matterNumber, matterNumbers]);



  const handleClientSearch = (event) => {
    let results = clients.filter((client) =>
    client?.toLowerCase().includes(event.query.toLowerCase())
    );
    dispatch(setFilteredClients(results));
    // Client edits trigger lookup through handleClientChange.
  };



  const handleMatterSearch = (event) => {
    let results = matterNumbers.filter((matterNumber) =>
    matterNumber?.toLowerCase().startsWith(event.query.toLowerCase())
    );
    dispatch(setFilteredMatters(results));
    // Don't auto-populate here - let the useEffect handle it when user selects
  };

  const editPopulatedRef = useRef(false);
  useEffect(() => {
    const populateFormForEditing =

    async () => {
      if (currentlyEditingEvent && !editPopulatedRef.current) {
        editPopulatedRef.current = true;

        // Direct state updates for simple fields
        dispatch(setTitle(currentlyEditingEvent.title || ''));
        dispatch(setNotes(currentlyEditingEvent.description || ''));
        dispatch(setLocation(currentlyEditingEvent.location || ''));
        dispatch(setAllday(currentlyEditingEvent.allDay || false));

        // Dates
        const startDate = currentlyEditingEvent.start ? new Date(eventFormDate(currentlyEditingEvent.start, currentlyEditingEvent.allDay)) : new Date();
        const endDate = currentlyEditingEvent.end ? new Date(currentlyEditingEvent.end) : new Date(startDate.getTime() + 60 * 60 * 1000);
        dispatch(setTimeZone(moment.tz.guess()));
        dispatch(setDate(startDate.toISOString()));
        dispatch(setDaterange([startDate.toISOString(), endDate.toISOString()]));

        // Matter Number and Client Name might need fetching/lookup
        // Set matter number — the matterNumber useEffect will auto-populate client name
        if (currentlyEditingEvent.case_id) {
          dispatch(setMatterNumber(currentlyEditingEvent.case_id));
        } else {
          dispatch(setMatterNumber(''));
          dispatch(setClientName(''));
        }

        // Category
        const category = categories.find((c) => c.name === currentlyEditingEvent.category_name);
        if (category) {
          dispatch(setSelectedCategory(category));
        } else {
          dispatch(setSelectedCategory(categories[0])); // Default category
        }

        // Other fields
        dispatch(setCreateTask(false)); // Reset task creation
        dispatch(setKey(Math.random().toString()));

        // Populate soft deadlines if the event has them
        if (currentlyEditingEvent.soft_deadlines && currentlyEditingEvent.soft_deadlines.length > 0) {
          dispatch(setSoftDeadlinesEnabled(true));
          dispatch(setSoftDeadlines(currentlyEditingEvent.soft_deadlines.map((sd) => ({
            tempId: `sd-${sd.id}`,
            id: sd.id,
            title: sd.title || '',
            date: sd.start ? eventFormDate(sd.start, sd.allDay) : new Date().toISOString(),
            daterange: [
            sd.start || new Date().toISOString(),
            sd.end || new Date(Date.now() + 3600000).toISOString()],

            allday: sd.allDay || false,
            selectedCategory: categories.find((c) => c.name === sd.category_name) || categories[0],
            notes: sd.description || '',
            assignedAttorneys: (sd.assignees || []).map((a) => ({
              id: a.attorney_id,
              name: a.attorney_name,
              profilePic: a.attorney_profile_pic,
              isPrimary: a.is_primary
            }))
          }))));
        } else {
          dispatch(setSoftDeadlinesEnabled(false));
          dispatch(setSoftDeadlines([]));
        }
      }
    };

    populateFormForEditing();

  }, [currentlyEditingEvent, dispatch, categories]);

  const categoryOptionTemplate =

  (option) => {
    return (
      <div className={`flex align-items-center`}>
                <span className={`inline-flex justify-content-center align-items-center mr-3 w-2rem h-2rem border-circle border-1 border-${option.color}-200 bg-${option.color}-100`}>
                    <AppIcon icon={`pi pi-calendar text-${option.color}-700`} />
                </span>
                <div>{option.name}</div>
            </div>);

  };



  return (
    <>
            <div className="field mb-3 col-6">
                <label htmlFor="client_name" className="font-medium text-900">Client Name</label>
                <AutoComplete
          inputId="client_name"
          value={clientName}
          suggestions={filteredClients}
          completeMethod={handleClientSearch}
          onChange={handleClientChange}
          placeholder='Client Name' />

            </div>

            <div className="field mb-3 col-6">
                <label htmlFor="matter_number" className="font-medium text-900">Matter Number</label>
                <AutoComplete
          inputId="matter_number"
          value={matterNumber}
          suggestions={filteredMatters}
          completeMethod={handleMatterSearch}
          onChange={handleMatterChange}
          placeholder='Matter Number' />

            </div>
            <div className="field mb-3 col-6">
                <label htmlFor="title" className="font-medium text-900">Title</label>
                <InputText id="title" type="text" value={title} onChange={(e) => dispatch(setTitle(e.target?.value))}
        placeholder='Title before dash' />
            </div>
            <div className="field mb-3 col-6">
                <label htmlFor="title2" className="font-medium text-900">Title 2</label>
                <InputText id="title2" type="text2" value={title2} onChange={(e) => dispatch(setTitle2(e.target?.value))}
        placeholder='Title after dash' />
            </div>
            <div className="field mb-3 col-6">
                <label htmlFor="location" className="font-medium text-900">Location</label>
                <InputText id="location" type="text" value={location} onChange={(e) => dispatch(setLocation(e.target?.value))}
        placeholder='Location' />
            </div>
            <div className="field mb-3 col-6">
                <label htmlFor="customer_email" className="font-medium text-900">Category</label>
                <Dropdown value={selectedCategory} onChange={(e) => dispatch(setSelectedCategory(e.value))} options={categories} optionLabel="name"
        placeholder="Select a Category" itemTemplate={categoryOptionTemplate} className="custom-dropdown" />
            </div>
        </>);

};

export default PrimaryInfo;
